import type { MapleOSProduct } from '@/components/billing/mapleosProducts';
import type { SeoPayload } from '@/types/ssr';

type DocsProgramSeoInput = {
  id?: string;
  label: string;
  description?: string | null;
};

type DocsSeoOptions = {
  program?: DocsProgramSeoInput | null;
  sectionId?: string | null;
};

const DOCS_SECTION_META: Record<string, { title: string; description: string }> = {
  welcome: {
    title: 'Welcome & Philosophy',
    description: 'Learn what MapleOS is, how it thinks about human control, and why it is designed as a desktop-first workspace.',
  },
  'how-it-works': {
    title: 'How MapleOS Works',
    description: 'Understand the MapleOS desktop model, windows, apps, files, and workflow structure.',
  },
  'privacy-control': {
    title: 'Privacy & Control',
    description: 'Explore Trust Mode, Sealed Memory, permission-aware access, and how MapleOS keeps people in control.',
  },
  apps: {
    title: 'Apps',
    description: 'Browse how MapleOS apps fit together inside the desktop and app launcher experience.',
  },
  'knowledge-memory': {
    title: 'Knowledge & Memory',
    description: 'See how MapleOS separates grounded knowledge from longer-lived memory for safer AI workflows.',
  },
  blueprints: {
    title: 'Blueprints',
    description: 'Learn how MapleOS uses blueprints to make automation visible, repeatable, and permission-aware.',
  },
  'power-users': {
    title: 'Power Users',
    description: 'Read how MapleOS supports terminal workflows, direct control, and advanced local-first usage.',
  },
  architecture: {
    title: 'Architecture Overview',
    description: 'Review the MapleOS architecture and how its desktop, apps, and system model fit together.',
  },
  'app-model': {
    title: 'App Model & Deep Links',
    description: 'Understand MapleOS app IDs, deep links, and how apps are opened and addressed inside the platform.',
  },
  'ui-guidelines': {
    title: 'UI/Design Guidelines',
    description: 'Learn the interface and design principles used across MapleOS apps and desktop surfaces.',
  },
  automation: {
    title: 'Automation & Blueprints',
    description: 'See how MapleOS approaches automation, blueprints, and developer-facing workflow orchestration.',
  },
  packaging: {
    title: 'Packaging & Maple Market',
    description: 'Learn how MapleOS packages apps, supports HTML bundles and .mpak installs, and handles marketplace delivery.',
  },
  faq: {
    title: 'Roadmap & FAQ',
    description: 'Read the current MapleOS developer FAQ and roadmap-oriented packaging guidance.',
  },
};

const SITE_URL = 'https://mapleos.ai';
const DEFAULT_IMAGE = '/images/mapleos-screenshot.png';
const DEFAULT_IMAGE_URL = `${SITE_URL}${DEFAULT_IMAGE}`;

function stripHtml(value?: string | null) {
  return String(value || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

function summarize(value?: string | null, maxLength = 160) {
  const clean = stripHtml(value);
  if (!clean) {
    return '';
  }

  if (clean.length <= maxLength) {
    return clean;
  }

  return `${clean.slice(0, maxLength).trimEnd()}…`;
}

function webPageStructuredData({
  name,
  description,
  path,
}: {
  name: string;
  description: string;
  path: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name,
    description,
    url: `${SITE_URL}${path}`,
  };
}

export function getHomeSeo(): SeoPayload {
  const title = 'MapleOS | Human-First Browser Desktop for Privacy, Trust and Control';
  const description =
    'MapleOS is a human-first browser desktop built for privacy, trust, and control. Manage files, run apps, write, build, and create in one local-first workspace.';

  return {
    title,
    description,
    keywords:
      'MapleOS, human-first desktop, privacy-first desktop, local-first operating system, browser desktop, sovereign computing, digital sovereignty, personal operating system, creative workspace, developer desktop',
    imageUrl: DEFAULT_IMAGE,
    url: '/',
    structuredData: [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'MapleOS',
        url: SITE_URL,
        logo: `${SITE_URL}/images/mapleos/MapleOS-LaunchLogo.png`,
        sameAs: ['https://github.com/CanXPAI/mapleos-your-digital-canvas', 'https://x.com/mapleosai'],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'MapleOS',
        applicationCategory: 'BrowserApplication',
        operatingSystem: 'Web, Windows, macOS, Linux',
        url: SITE_URL,
        image: DEFAULT_IMAGE_URL,
        description,
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'MapleOS',
        url: SITE_URL,
      },
    ],
  };
}

export function getProductsSeo(url: '/products' | '/purchase' = '/products'): SeoPayload {
  const isPublicProductsPage = url === '/products';
  const title = isPublicProductsPage
    ? 'Products | MapleOS Editions, Pricing and Installers'
    : 'Purchase | MapleOS Products and Marketplace';
  const description = isPublicProductsPage
    ? 'Compare MapleOS 26, MapleOS Pro 26, and MapleOS Enterprise 26 pricing, included installers, and edition benefits.'
    : 'Explore MapleOS editions, pricing, installers, and Marketplace-related purchases from one place.';

  return {
    title,
    description,
    keywords:
      'MapleOS products, MapleOS pricing, MapleOS editions, MapleOS 26, MapleOS Pro 26, MapleOS Enterprise 26, desktop installer, bare metal installer',
    imageUrl: DEFAULT_IMAGE,
    url,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: title,
      description,
      url: `${SITE_URL}${url}`,
    },
  };
}

export function getProductSeo(product: MapleOSProduct): SeoPayload {
  const title = `${product.name} | Pricing, Features and Installer Options`;
  const description = `${product.name} — ${product.description}`;
  const url = `/products/${product.id}`;

  return {
    title,
    description,
    keywords: `${product.name}, MapleOS pricing, MapleOS installer, ${product.shortName}, ${product.audience}`,
    imageUrl: DEFAULT_IMAGE,
    url,
    structuredData: [
      webPageStructuredData({
        name: title,
        description,
        path: url,
      }),
      {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product.name,
        description: product.description,
        category: 'SoftwareApplication',
        brand: {
          '@type': 'Brand',
          name: 'MapleOS',
        },
        image: DEFAULT_IMAGE_URL,
        url: `${SITE_URL}${url}`,
        offers: {
          '@type': 'Offer',
          priceCurrency: 'USD',
          price: (product.priceCents / 100).toFixed(2),
          availability: 'https://schema.org/InStock',
          url: `${SITE_URL}${url}`,
        },
      },
    ],
  };
}

export function getNewsSeo(): SeoPayload {
  const title = 'News | MapleOS Announcements, Releases and Updates';
  const description = 'Read MapleOS announcements, product updates, feature releases, research, and community news.';

  return {
    title,
    description,
    keywords: 'MapleOS news, MapleOS updates, MapleOS releases, product announcements, community updates',
    imageUrl: DEFAULT_IMAGE,
    url: '/news',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: title,
      description,
      url: `${SITE_URL}/news`,
    },
  };
}

export function getForumsSeo(options?: {
  threadId?: string | null;
  threadTitle?: string | null;
  firstPostContent?: string | null;
}): SeoPayload {
  const threadTitle = options?.threadTitle?.trim();
  const threadSnippet = summarize(options?.firstPostContent, 155);
  const title = threadTitle ? `${threadTitle} | MapleOS Forums` : 'Forums | MapleOS Community Discussions';
  const description = threadTitle && threadSnippet
    ? threadSnippet
    : 'Join MapleOS community discussions, ask questions, share product feedback, and discover new ways to use the platform.';
  const url = options?.threadId ? `/forums?thread=${encodeURIComponent(options.threadId)}` : '/forums';

  return {
    title,
    description,
    keywords: 'MapleOS forums, MapleOS community, MapleOS discussions, product feedback, user support forum',
    imageUrl: DEFAULT_IMAGE,
    url,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: title,
      description,
      url: `${SITE_URL}${url}`,
    },
  };
}

export function getBugsSeo(): SeoPayload {
  const title = 'Bug Reports | MapleOS Issue Tracker and Status';
  const description = 'Report MapleOS bugs and follow active or resolved issues in the public issue tracker.';

  return {
    title,
    description,
    keywords: 'MapleOS bugs, MapleOS issue tracker, report bugs, bug status, software issues',
    imageUrl: DEFAULT_IMAGE,
    url: '/bugs',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: title,
      description,
      url: `${SITE_URL}/bugs`,
    },
  };
}

export function getPartnershipSeo(): SeoPayload {
  const title = 'Partnerships | MapleOS AI Ecosystem and Deployment';
  const description =
    'Explore MapleOS partnerships for developers, enterprise teams, and ecosystem collaborators building and deploying AI systems in the real world.';

  return {
    title,
    description,
    keywords: 'MapleOS partnership, AI partnerships, enterprise AI deployment, developer partnership, ecosystem collaboration',
    imageUrl: '/images/partnership-deck-thumbnail.png',
    url: '/partnership',
    structuredData: webPageStructuredData({
      name: title,
      description,
      path: '/partnership',
    }),
  };
}

export function getDocsSeo(options?: DocsSeoOptions): SeoPayload {
  const program = options?.program ?? null;
  const sectionId = options?.sectionId ?? null;
  const sectionMeta = sectionId ? DOCS_SECTION_META[sectionId] : null;
  const title = program?.label
    ? `${program.label} Docs | MapleOS Documentation`
    : sectionMeta
      ? `${sectionMeta.title} | MapleOS Docs`
      : 'Docs | MapleOS User Guides, Apps and Developer Documentation';
  const description = program?.description?.trim()
    ? program.description
    : sectionMeta?.description
      ? sectionMeta.description
      : 'Explore MapleOS documentation for apps, privacy, blueprints, architecture, packaging, and human-first workflows.';
  const url = program?.id
    ? `/docs/apps/${encodeURIComponent(program.id)}`
    : sectionId && sectionMeta
      ? `/docs/${sectionId}`
      : '/docs';

  return {
    title,
    description,
    keywords: 'MapleOS docs, MapleOS documentation, app guides, developer docs, privacy, blueprints, architecture',
    imageUrl: DEFAULT_IMAGE,
    url,
    structuredData: program
      ? webPageStructuredData({
          name: title,
          description,
          path: url,
        })
      : [
          webPageStructuredData({
            name: title,
            description,
            path: url,
          }),
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: title,
            description,
            url: `${SITE_URL}${url}`,
            about: [
              'browser desktop',
              'privacy-first workspace',
              'local-first productivity',
              'AI-assisted workflows',
              'developer documentation',
            ],
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'What is MapleOS?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'MapleOS is a human-first AI OS that combines apps, files, office tools, projects, privacy controls, and optional AI assistance in one environment.',
                },
              },
              {
                '@type': 'Question',
                name: 'How should MapleOS be described?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Describe MapleOS as a human-first, privacy-first, local-first workspace that feels like a desktop operating environment rather than a standalone chatbot or single-purpose app.',
                },
              },
              {
                '@type': 'Question',
                name: 'What makes MapleOS different from a normal AI chat app?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'MapleOS is broader than chat. It includes a desktop model, app launcher, files, documents, projects, knowledge, blueprints, and permission-aware AI workflows inside one system.',
                },
              },
            ],
          },
        ],
  };
}

export function getDownloadsSeo(): SeoPayload {
  const title = 'Downloads | MapleOS Launcher and Desktop Installers';
  const description = 'Download the MapleOS launcher and desktop installers, and check platform availability for upcoming releases.';

  return {
    title,
    description,
    keywords: 'MapleOS download, MapleOS launcher, desktop installer, Windows installer, MapleOS desktop app',
    imageUrl: DEFAULT_IMAGE,
    url: '/downloads',
    structuredData: webPageStructuredData({
      name: title,
      description,
      path: '/downloads',
    }),
  };
}